import { useState } from 'react';

import logo from '@Assets/images/logo-80.png';

import { GOOGLE_PLAY_URL } from '../config/links';
import { useMobileOS } from '../hooks/useMobileOS';
import { useVisitorCountry } from '../hooks/useVisitorCountry';
import { captureFunnelEvent, trackButtonClick } from '../services/telemetry';

/** localStorage flag so a dismissed banner stays dismissed across pages/reloads. */
const DISMISSED_KEY = 'pml_openInGooglePlayBanner_dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

interface OpenInGooglePlayBannerProps {
  source: string;
}

/**
 * Top-of-page prompt inviting Android visitors to get / open the native app on
 * Google Play. Shown only when:
 *   - the browser is Android (iOS visitors get the waitlist banner instead), and
 *   - the visitor is not in mainland China (IP-based; Play is unavailable there),
 *     and
 *   - the visitor hasn't dismissed it before.
 *
 * The app is already live on Google Play, so this links straight to the store
 * listing; if the app is installed, Play surfaces an "Open" button there. We
 * still show the banner to users who may already have the app installed —
 * that's an intentional product decision — and it's closable via the ×.
 *
 * The country lookup fails open: if geo can't be resolved we still show the
 * banner rather than hide it.
 */
function OpenInGooglePlayBanner({ source }: OpenInGooglePlayBannerProps) {
  const os = useMobileOS();
  const { country, resolved } = useVisitorCountry();
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Ignore storage failures (private mode / disabled) — hide for this session.
    }
  };

  // Gate: Android only, region resolved and not China, not dismissed.
  if (dismissed) return null;
  if (os !== 'android') return null;
  if (!resolved) return null; // avoid flashing before we know the country
  if (country === 'CN') return null;

  return (
    <div
      className={`
        relative flex w-full items-center gap-3 border-b border-gray-200
        bg-white px-4 py-2 font-[Outfit]
      `}
      role="region"
      aria-label="Get the app on Google Play"
    >
      <img
        src={logo}
        alt=""
        aria-hidden="true"
        className="h-9 w-9 shrink-0 rounded-lg"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-800">
          PineappleMusicLab App is Live on Google Play!
        </p>
        <p className="truncate text-xs text-gray-500">
          Camera scan → piano fingering, on the go
        </p>
      </div>
      <a
        href={GOOGLE_PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          captureFunnelEvent('google_play_banner_clicked', { source });
          trackButtonClick(source, 'Open in Google Play');
        }}
        className={`
          shrink-0 rounded-full bg-yellow-400 px-4 py-1.5 text-sm font-semibold
          text-gray-800 transition-transform duration-100
          hover:bg-yellow-500
          active:scale-95
        `}
      >
        Open
      </a>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className={`
          flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xl
          leading-none text-gray-400
          hover:bg-gray-100 hover:text-gray-700
        `}
      >
        ×
      </button>
    </div>
  );
}

export { OpenInGooglePlayBanner };
